import {Injectable} from '@angular/core';
import {ActivationEnd, ActivationStart, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {BehaviorSubject} from 'rxjs';
import {
	AUTH_PATH,
	DEFAULT_THEME,
	JOURNAL_PATH,
	ROOT_PATH,
	SANDBOX_PATH,
	SETTINGS_PATH,
	TEMPLATES_PATH,
	TEST_DASH_PATH,
	USER_SELECT_PATH
} from 'shared/magic';
import {LoadableComponent} from '../_interface/loadableComponent';
import {Store} from '@ngrx/store';
import * as layoutActions from '../_actions/layout.actions';
import * as fromLayout from '../_selectors/layout.selectors';
import {distinctUntilChanged, filter, map, skipUntil, switchMap} from 'rxjs/operators';

interface ThemeMap {
	for: string[];
	slug: string;
}

const THEME_MAP: ThemeMap[] = [
	{
		for: [JOURNAL_PATH],
		slug: 'default'
	},
	{
		for: [USER_SELECT_PATH, TEST_DASH_PATH, ROOT_PATH, AUTH_PATH],
		slug: 'light'
	},
	{
		for: [SETTINGS_PATH, `${JOURNAL_PATH}/${TEMPLATES_PATH}`, `${SETTINGS_PATH}/${JOURNAL_PATH}`, `${SANDBOX_PATH}/form-inputs-test`],
		slug: 'dark'
	}
];

@Injectable({
	providedIn: 'root'
})
export class LayoutService {
	currentTheme$ = new BehaviorSubject(null);
	currentHeader$ = new BehaviorSubject<LoadableComponent>(null);
	currentToolbox$ = new BehaviorSubject<LoadableComponent>(null);
	currentBottomBar$ = new BehaviorSubject<LoadableComponent>(null);
	currentFloatingSidebar$ = new BehaviorSubject<LoadableComponent>(null);
	currentEditEntry$ = new BehaviorSubject<LoadableComponent>(null);
	bottomBarToggled$ = this.store.select(fromLayout.selectBottomBarVisible);
	floatingSidebarToggled$ = this.store.select(fromLayout.selectFloatingSidebarVisible);
	splashVisible$ = new BehaviorSubject<string>(null);

	constructor(private router: Router, private store: Store) {
		this.applyThemeBasedPath();
	}

	applyHeader(headerDef: LoadableComponent) {
		this.currentHeader$.next(headerDef);
	}

	applyToolbox(toolboxDef: LoadableComponent) {
		this.currentToolbox$.next(toolboxDef);
	}

	applyBottomBar(bottomBarDef: LoadableComponent) {
		this.currentBottomBar$.next(bottomBarDef);
	}

	applyFloatingSidebar(floatingBarDef: LoadableComponent) {
		this.currentFloatingSidebar$.next(floatingBarDef);
	}

	applyEditEntry(editEntryDef: LoadableComponent) {
		this.currentEditEntry$.next(editEntryDef);
	}

	applyThemeBasedPath() {
		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.currentTheme$.next(this.getTheme((event as NavigationEnd).url));
			}
		});
	}

	getTheme(url: string) {
		const first = url.split('/')[1];
		const second = url.split('/')[2];
		const theme = THEME_MAP.find(k => k.for.includes(`${first}/${second}`)) || THEME_MAP.find(k => k.for.includes(first));
		return (!first || !theme) ? DEFAULT_THEME : theme.slug;
	}

	toggleFloatingSidebar = () => this.store.dispatch(layoutActions.toggleFloatingSidebar());

	splashControl = () => {
		const router$ = this.router.events;
		const navStart$ = router$.pipe(filter(event => event instanceof NavigationStart));
		const navEnd$ = router$.pipe(filter(event => event instanceof NavigationEnd));
		const actStart$ = router$.pipe(
			filter(event => event instanceof ActivationStart && event.snapshot.data?.splashFor),
			distinctUntilChanged((event: ActivationStart) => event.snapshot.data?.splashFor)
		);
		const actEnd$ = router$.pipe(
			filter(event => event instanceof ActivationEnd && event.snapshot.data?.splashFor),
		);

		const start$ = navStart$.pipe(
			switchMap((navigationStart: NavigationStart) => actStart$.pipe(
				map((activationStart) => ({
					splashFor: activationStart.snapshot.data?.splashFor,
					navigationStart,
					activationStart
				}))
			))
		);

		const end$ = actEnd$.pipe(
			skipUntil(navStart$),
			switchMap((activationEnd: ActivationEnd) => navEnd$.pipe(
				map(navigationEnd => ({
					activationEnd,
					navigationEnd,
					splashFor: activationEnd.snapshot.data?.splashFor
				}))
			))
		);

		navStart$.subscribe(() => this.splashVisible$.next(null));
		start$.subscribe(({splashFor}) => this.splashVisible$.next(splashFor));
		end$.subscribe(() => this.splashVisible$.next(null));
	}
}
